// Use if we want to enable chunks in the future
// eslint-disable-next-line no-undef
// __webpack_public_path__ = window.assetsPath;

import deepmerge from "deepmerge";

import { Snap } from "@searchspring/snap-preact";
import { getContext } from "@searchspring/snap-toolbox";
import { dynamicVariants } from './dynamic-variants.js';

import { afterStore } from "./middleware/plugins/afterStore.js";
import { configurable } from "./middleware/plugins/configurable.js";
import { combineMerge } from "./middleware/functions.js";
import { ContentSkel } from "./components/Content/Skel.js";
import { SidebarSkel } from "./components/Sidebar/Skel.js";



const context = getContext(["collection", "tags", "template", "shopper", "prefetchSearchResults"]);

const backgroundFilters = [];

if (context.collection?.handle) {
  // set background filters
  if (context.collection.handle != "all") {
    backgroundFilters.push({
      field: "collection_handle",
      value: context.collection.handle,
      type: "value",
      background: true,
    });
  }

  // handle collection tags (filters)
  if (context.tags) {
    var collectionTags = context.tags
      .toLowerCase()
      .replace(/-/g, "")
      .replace(/ +/g, "")
      .split("|");
    collectionTags.forEach((tag) => {
      backgroundFilters.push({
        field: "ss_tags",
        value: tag,
        type: "value",
        background: true,
      });
    });
  }

  //Gender background filter logic
  if(window.Resources.searchspring.collection.genderSelected != null) {
    backgroundFilters.push({
      field: "variant_mfield_cql_gender",
      value: window.Resources.searchspring.collection.genderSelected,
      type: "value",
      background: true,
    });
  }
}

backgroundFilters.push({
  field: "not_lensadvizor",
  value: 1,
  type: "value",
  background: true,
});

let dynamicVariantsConfig = {
  field: 'ss_swatches',
  limit: 4,
  swap: function(result, variant) {
      
      const core = result.mappings.core;
      const { attributes, custom } = result;
      
      if(variant.image) {
          core.imageUrl = variant.image;
      }

      custom.variantSelected = variant;
  }
  };


/*
	configuration and instantiation
 */
const siteId = window.Shopify.locale === "es" ? "42sbcb" : "qxjgzl" ;

let config = {
  url: {
    settings: {
      coreType: "query",
      customType: "query",
    },
    parameters: {
      core: {
        query: { name: "q" },
        page: { name: "p" },
      },
    },
  },
  client: {
    globals: {
      siteId: siteId,
    },
  },
  instantiators: {
    recommendation: {
      components: {
        Default: async () => {
          return (await import("./components/Recommendations/Recs/Recs.js")).Recs;
        },
      },
      config: {
        branch: 'develop',
      },
    },
  },
  controllers: {
    search: [
      {
        config: {
          id: 'search',
          plugins: [[afterStore], [configurable], [dynamicVariants, dynamicVariantsConfig]],
          settings: {
            redirects: {
              merchandising: true,
            },
            infinite: {
              // Arbitrarily high backfill value to ensure if the user got to page 100 and clicked into the PDP, the back button would work as expected.
              // At the time this was coded, there were approximately 1140 products in total in Rudis, so this gives us enough wiggle room for the
              // catalog to double in size.
              //
              // Yes, if the user ever _did_ get that far, they would be waiting a while for the backfill to be complete, but boy would they be glad they
              // didn't have to scroll through those thousands of products again to find where they left off.
              backfill: 100
            },
            restorePosition: {
              enabled: true,
              onPageShow: true
            },
            facets: {
              pinFiltered: false,
            },
          },
          globals: {
            filters: backgroundFilters,
            pagination: {
              pageSize: window?.ssConfig?.pageSize ?? 24,
            },
            ssConfig: window?.ssConfig,
          },
        },
        targeters: [
          {
            selector: '#searchspring-content',
            hideTarget: true,
            prefetch: context.prefetchSearchResults ?? false,
            component: async () => {
              return (await import('./components/Content/Content.js')).Content;
            },
          },
        ],
      },
    ],
    autocomplete: [
      {
        config: {
          id: 'autocomplete',
          selector: '.header__icons .search-modal__content .search__input',
          settings: {
            syncInputs: false,
            trending: {
              limit: 6,
              showResults: true,
            },
          },
          globals: {
            filters: backgroundFilters,
            merchandising: {
              disableInlineBanners: true,
              redirect: ''
            },
            search: {
              query: {
                spellCorrection: true,
              },
            },
          },
        },
        targeters: [
          {
            selector: '.header__icons .search-modal__content .search__input',
            component: async () => {
              return (await import("./components/Autocomplete/Autocomplete.jsx"))
                .Autocomplete;
            },
          },
        ],
      },
      {
        config: {
          id: 'autocomplete-mobile',
          selector: '.header__icons .search-modal__content .search__input',
          settings: {
            syncInputs: false,
            trending: {
              limit: 6,
              showResults: true,
            },
          },
          globals: {
            filters: backgroundFilters,
            merchandising: {
              disableInlineBanners: true,
              redirect: ''
            },
            search: {
              query: {
                spellCorrection: true,
              },
            },
          },
        },
        targeters: [
          {
            selector: '.header__icons .search-modal__content .search__input',
            component: async () => {
              return (await import("./components/Autocomplete/Autocomplete.jsx"))
                .Autocomplete;
            },
          },
        ],
      },
    ],
    finder: [
      {
        config: {
          id: "finder",
          url: "/",
          fields: [
            {
              field: "generic_color",
              label: "Color",
            },
          ],
        },
        targeters: [
          {
            name: "finder",
            selector: "#searchspring-finder",
            component: async () => {
              return (await import("./components/Finder/Finder.js")).Finder;
            },
          },
        ],
      },
      {
        config: {
          id: "finder_hierarchy",
          url: "/",
          fields: [
            {
              field: "ss_category_hierarchy",
            },
          ],
        },
        targeters: [
          {
            name: "finder_hierarchy",
            selector: "#searchspring-finder-hierarchy",
            component: async () => {
              return (await import("./components/Finder/Finder.js")).Finder;
            },
          },
        ],
      },
    ],
  },
};

// used to add config settings from cypress e2e tests
if (window?.mergeSnapConfig) {
  config = deepmerge(config, window.mergeSnapConfig, {
    arrayMerge: combineMerge,
  });
}

const snap = new Snap(config);
